import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
  Node,
  Edge,
  MarkerType,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import { toPng } from 'html-to-image';
import { message } from 'antd';
import { Helmet } from 'react-helmet-async';
import '@xyflow/react/dist/style.css';
import {
  Building2,
  UserCheck,
  Download,
  Maximize2,
  ZoomIn,
  ZoomOut,
  ChevronDown,
  X,
  Search,
  Plus,
  Minus,
} from 'lucide-react';
import UserSidebar, { USER_PAGE_ROW } from '../../components/SidebarNew/UserSidebar';
import Layout from '../../Layout';
import { UserAvatar } from '../Social/component/UserAvatar';
import { COMPANY_NAME } from '../../components/SidebarNew/userHub';
import {
  buildPositionTree, collectEmployees, countEmployees, filterLeaders, findNode, getAncestorsAndSelf, isLeader, searchOrg, type RawNode,
} from './orgTree';

// ==========================================
// 1. CẤU TRÚC DỮ LIỆU CÂY (TREE DATA)
// ==========================================
const initialTreeData: RawNode = {
  id: 'root-bgd',
  type: 'department',
  label: 'Ban Giám Đốc',
  count: 48,
  badgeColor: 'bg-amber-500',
  children: [
    {
      id: 'hanoi',
      type: 'department',
      label: 'Hà Nội',
      count: 1,
      badgeColor: 'bg-emerald-500',
      children: [
        {
          id: 'kd-hn3',
          type: 'department',
          label: 'Phòng Kinh Doanh HN3',
          count: 1,
          badgeColor: 'bg-emerald-500',
          children: [
            {
              id: 'emp-hn3-1',
              type: 'employee',
              name: 'Nguyễn Văn Nam',
              role: 'Trưởng phòng KD HN3',
              avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
            },
          ],
        },
      ],
    },
    {
      id: 'phong-ba',
      type: 'department',
      label: 'Phòng BA',
      count: 3,
      badgeColor: 'bg-amber-500',
      children: [
        {
          id: 'emp-ba-1',
          type: 'employee',
          name: '[Win Auto] Hải Cao',
          role: 'Giám đốc Marketing',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
        },
        {
          id: 'emp-ba-2',
          type: 'employee',
          name: 'Lưu Đức Bảo',
          role: 'Nhân viên marketing',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
        },
        {
          id: 'emp-ba-3',
          type: 'employee',
          name: 'Phạm Ngọc Đăng Khoa',
          role: 'Nhân viên marketing',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100',
        },
      ],
    },
    {
      id: 'phap-che',
      type: 'department',
      label: 'Phòng Pháp Chế',
      count: 5,
      badgeColor: 'bg-amber-500',
      children: [
        {
          id: 'emp-pc-1',
          type: 'employee',
          name: 'Phạm Đăng Khoa',
          role: 'Trưởng phòng pháp chế',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
        },
        {
          id: 'emp-pc-2',
          type: 'employee',
          name: 'Trần Thu Vân',
          role: 'Trưởng phòng pháp chế',
          avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
        },
        {
          id: 'emp-pc-3',
          type: 'employee',
          name: 'Lê Hoàng Nam',
          role: 'Chuyên viên pháp chế',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
        },
        {
          id: 'emp-pc-4',
          type: 'employee',
          name: 'Nguyễn Thị Mai',
          role: 'Nhân viên hồ sơ',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
        },
        {
          id: 'emp-pc-5',
          type: 'employee',
          name: 'Vũ Minh Tuấn',
          role: 'Tập sự pháp chế',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100',
        },
      ],
    },
    {
      id: 'hr-1office',
      type: 'department',
      label: 'Hành Chính Nhân Sự',
      count: 11,
      badgeColor: 'bg-amber-500',
      children: [
        {
          id: 'emp-hr-1',
          type: 'employee',
          name: 'Nguyễn Diệp',
          role: 'Trưởng phòng HCNS',
          avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100',
        },
        {
          id: 'emp-hr-2',
          type: 'employee',
          name: 'Lê Việt Thắng',
          role: 'Chuyên viên C&B',
          avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100',
        },
        {
          id: 'emp-hr-3',
          type: 'employee',
          name: 'Bàn Dầu An',
          role: 'Chuyên viên tuyển dụng',
          avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100',
        },
        {
          id: 'emp-hr-4',
          type: 'employee',
          name: 'Phan Đình Nguyên',
          role: 'Chuyên viên đào tạo',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
        },
        {
          id: 'emp-hr-5',
          type: 'employee',
          name: 'Trần Mỹ Duyên',
          role: 'Nhân viên hành chính',
          avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100',
        },
      ],
    },
    {
      id: 'kd-5',
      type: 'department',
      label: 'Phòng Kinh Doanh 5',
      count: 3,
      badgeColor: 'bg-amber-500',
      children: [
        {
          id: 'emp-kd5-1',
          type: 'employee',
          name: 'Vũ Đức A12',
          role: 'Trưởng phòng kinh doanh',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
        },
        {
          id: 'emp-kd5-2',
          type: 'employee',
          name: 'Nguyễn Hiền',
          role: 'Nhân viên kinh doanh',
          avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
        },
        {
          id: 'emp-kd5-3',
          type: 'employee',
          name: 'Trần Văn Mạnh',
          role: 'Nhân viên kinh doanh',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
        },
      ],
    },
    {
      id: 'pvh-marketing',
      type: 'department',
      label: 'PVH - PHÒNG MARKETING',
      count: 2,
      badgeColor: 'bg-blue-500',
      children: [
        {
          id: 'emp-pvh-1',
          type: 'employee',
          name: 'Hoàng Văn Tuyền',
          role: 'Tổng Giám đốc',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
        },
        {
          id: 'emp-pvh-2',
          type: 'employee',
          name: 'Đặng Quốc Huy',
          role: 'Content Creator',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
        },
      ],
    },
  ],
};

// ==========================================
// 2. MODAL DANH SÁCH NHÂN SỰ CỦA PHÒNG BAN (cả phòng ban con)
// ==========================================
const MembersModal = ({
  isOpen,
  onClose,
  department,
  onPickEmployee,
}: {
  isOpen: boolean;
  onClose: () => void;
  department: RawNode | null;
  onPickEmployee: (id: string) => void;
}) => {
  const [q, setQ] = useState('');
  useEffect(() => { setQ(''); }, [department?.id]);
  if (!isOpen || !department) return null;

  const all = collectEmployees(department);
  const k = q.trim().toLowerCase();
  const members = k ? all.filter(m => `${m.node.name} ${m.node.role}`.toLowerCase().includes(k)) : all;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/65 p-3 sm:p-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-[92vw] max-w-md max-h-[80vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700" role="dialog" aria-modal="true">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate pr-2">{department.label}</h3>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-400 font-medium">{all.length} nhân sự</span>
            <button onClick={onClose} aria-label="Đóng" className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full cursor-pointer">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {all.length > 5 && (
          <div className="px-3 pt-3">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Tìm theo tên, chức danh..." autoFocus
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-orange-500" />
          </div>
        )}
        <div className="overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/60 p-2">
          {members.length === 0 && <div className="text-xs text-slate-400 text-center py-6">{all.length ? 'Không tìm thấy nhân sự' : 'Phòng ban chưa có nhân sự'}</div>}
          {members.map(({ node: m, path }) => (
            <button key={m.id} type="button" onClick={() => onPickEmployee(m.id)}
              className="w-full text-left flex items-center gap-3 p-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition cursor-pointer">
              <UserAvatar name={m.name || ''} src={m.avatar} size={36} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{m.name}</div>
                <div className="text-[11px] text-slate-400 truncate">{[m.role || 'Nhân viên', ...path.slice(1)].join(' · ')}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Thẻ thông tin nhân sự khi bấm vào một người trên sơ đồ
const EmployeeCard = ({ emp, path, onClose, onShowDept }: { emp: RawNode; path: string[]; onClose: () => void; onShowDept: () => void }) => (
  <div className="absolute top-3 right-3 z-40 w-[280px] max-w-[calc(100%-1.5rem)] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-4">
    <button onClick={onClose} aria-label="Đóng" className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"><X className="w-4 h-4" /></button>
    <div className="flex items-center gap-3">
      <UserAvatar name={emp.name || ''} src={emp.avatar} size={48} />
      <div className="min-w-0">
        <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{emp.name}</div>
        <div className="text-xs text-slate-500 truncate">{emp.role || 'Nhân viên'}</div>
      </div>
    </div>
    <div className="mt-3 text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
      <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{[COMPANY_NAME, ...path.slice(1)].join(' › ')}</span>
    </div>
    {isLeader(emp.role) && <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 px-2 py-0.5 rounded">Quản lý</span>}
    {path.length > 0 && (
      <button onClick={onShowDept} className="mt-3 w-full text-xs font-semibold text-orange-600 border border-orange-200 dark:border-orange-900 rounded-lg py-1.5 hover:bg-orange-50 dark:hover:bg-orange-950/30 cursor-pointer">
        Xem cả phòng {path[path.length - 1]}
      </button>
    )}
  </div>
);

// ==========================================
// 3. CUSTOM NODES
// ==========================================
const DepartmentNode = ({ data }: any) => {
  const isSelected = data.isSelected;

  return (
    <div
      onClick={() => data.onSelectNode?.(data.id)}
      className={`group relative px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-slate-800 rounded-full flex items-center justify-between w-auto min-w-[150px] sm:min-w-[210px] max-w-[260px] sm:max-w-[320px] shadow-sm border transition-all cursor-pointer ${
        isSelected
          ? 'border-2 border-orange-500 ring-4 ring-orange-100 dark:ring-orange-950/40'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <Handle type="target" position={Position.Left} className="!bg-transparent !border-0 !w-1 !h-1" />

      <span className="font-semibold text-[11px] sm:text-xs text-slate-800 dark:text-slate-100 uppercase tracking-wide mr-2 break-words whitespace-normal leading-relaxed select-none">
        {data.label}
      </span>

      <div className="flex items-center gap-1.5 shrink-0 self-center">
        {data.count !== undefined && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onOpenMembers?.(data);
            }}
            className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-[11px] font-bold text-white transition-transform active:scale-95 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 dark:hover:ring-slate-600 ${
              data.badgeColor || 'bg-emerald-500'
            }`}
          >
            {data.count}
          </button>
        )}
      </div>

      {data.hasChildren && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleExpand?.();
          }}
          className="opacity-95 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity absolute -right-2 customer-h-center w-4 h-4 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-orange-500 hover:text-orange-500 text-slate-500 dark:text-slate-400 flex items-center justify-center shadow-sm cursor-pointer z-20"
        >
          {data.isExpanded ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        </button>
      )}

      <Handle type="source" position={Position.Right} className="!bg-transparent !border-0 !w-1 !h-1" />
    </div>
  );
};

const EmployeeNode = ({ data }: any) => {
  const isSelected = data.isSelected;

  return (
    <div
      onClick={() => data.onSelectNode?.(data.id)}
      className={`group relative px-2.5 sm:px-3 py-1.5 bg-white dark:bg-slate-800 rounded-full flex items-center gap-2 w-auto min-w-[150px] sm:min-w-[210px] max-w-[260px] sm:max-w-[320px] shadow-sm border transition-all cursor-pointer ${
        isSelected
          ? 'border-2 border-orange-500 ring-4 ring-orange-100 dark:ring-orange-950/40'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <Handle type="target" position={Position.Left} className="!bg-transparent !border-0 !w-1 !h-1" />

      <UserAvatar name={data.name || ''} src={data.avatar} size={30} className="border border-slate-200 dark:border-slate-700" />
      <div className="flex flex-col text-left min-w-0">
        <span className="font-bold text-[11px] sm:text-xs text-slate-800 dark:text-slate-100 leading-tight truncate">{data.name}</span>
        <span className="text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-500 font-medium truncate">{data.role}</span>
      </div>

      {data.hasChildren && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleExpand?.();
          }}
          className="opacity-95 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-orange-500 hover:text-orange-500 text-slate-500 dark:text-slate-400 flex items-center justify-center shadow-sm cursor-pointer z-20"
        >
          {data.isExpanded ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        </button>
      )}

      <Handle type="source" position={Position.Right} className="!bg-transparent !border-0 !w-1 !h-1" />
    </div>
  );
};

const nodeTypes = {
  department: DepartmentNode,
  employee: EmployeeNode,
};

// ==========================================
// 4. LAYOUT CÂY
// ==========================================
function buildTreeLayout(
  root: RawNode,
  expandedIds: Set<string>,
  selectedNodeId: string | null,
  onToggleExpand: (id: string) => void,
  onOpenMembers: (dept: any) => void,
  onSelectNode: (id: string) => void,
  isMobile: boolean = false,
  isDarkMode: boolean = false
) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  
  const xSpacing = isMobile ? 180 : 300;
  const ySpacing = isMobile ? 45 : 65;

  let currentY = 10;
  const edgeColor = isDarkMode ? '#475569' : '#cbd5e1';

  function processNode(item: RawNode, depth: number, parentId: string | null): number {
    const hasChildren = Boolean(item.children && item.children.length > 0);
    const isExpanded = expandedIds.has(item.id);

    let nodeY = 0;

    if (hasChildren && isExpanded && item.children) {
      const childYs: number[] = [];
      item.children.forEach((child) => {
        childYs.push(processNode(child, depth + 1, item.id));
      });
      nodeY = (childYs[0] + childYs[childYs.length - 1]) / 2;
    } else {
      nodeY = currentY;
      currentY += ySpacing;
    }

    nodes.push({
      id: item.id,
      type: item.type,
      position: { x: depth * xSpacing + (isMobile ? 5 : 30), y: nodeY },
      draggable: false,
      data: {
        ...item,
        count: item.type === 'department' ? countEmployees(item) : undefined,
        hasChildren,
        isExpanded,
        isSelected: item.id === selectedNodeId,
        onToggleExpand: () => onToggleExpand(item.id),
        onOpenMembers,
        onSelectNode,
      },
    });

    if (parentId) {
      edges.push({
        id: `e-${parentId}-${item.id}`,
        source: parentId,
        target: item.id,
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 8,
          height: 8,
          color: edgeColor,
        },
        style: { stroke: edgeColor, strokeWidth: 1.5 },
      });
    }

    return nodeY;
  }

  processNode(root, 0, null);
  return { nodes, edges };
}

// ==========================================
// 5. MAIN CANVAS CONTROLLER
// ==========================================
const DEFAULT_EXPANDED_DEPT = ['root-bgd', 'phong-ba', 'hanoi', 'phap-che', 'hr-1office', 'kd-5'];
const DEFAULT_EXPANDED_POS = ['pos-root', 'pos-level-0', 'pos-level-1'];

const OrgCanvas = () => {
  const { zoomIn, zoomOut, fitView, setCenter, getZoom, getNode, getNodes } = useReactFlow();

  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const viewDropdownRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const checkDark = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) setShowSearchPopover(false);
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(event.target as Node)) setShowViewDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  const [viewType, setViewType] = useState<'department' | 'position'>('department');
  const [onlyLeader, setOnlyLeader] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    new Set(typeof window !== 'undefined' && window.innerWidth < 768 ? ['root-bgd', 'phong-ba'] : DEFAULT_EXPANDED_DEPT));

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>('phong-ba');
  const [selectedDeptForModal, setSelectedDeptForModal] = useState<RawNode | null>(null);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showSearchPopover, setShowSearchPopover] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoomLevel, setZoomLevel] = useState(100);

  // Cây đang hiển thị: theo phòng ban hoặc theo chức vụ, có thể chỉ giữ quản lý
  const tree = useMemo(() => {
    const base = onlyLeader ? filterLeaders(initialTreeData)! : initialTreeData;
    return viewType === 'position' ? buildPositionTree(base, COMPANY_NAME) : base;
  }, [viewType, onlyLeader]);

  const changeView = (v: 'department' | 'position') => {
    setViewType(v);
    setExpandedIds(new Set(v === 'position' ? DEFAULT_EXPANDED_POS : DEFAULT_EXPANDED_DEPT));
    setSelectedNodeId(null);
    setShowViewDropdown(false);
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 80);
  };

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleOpenMembers = useCallback((dept: any) => {
    setSelectedDeptForModal(findNode(tree, dept.id));
  }, [tree]);

  const centerOn = useCallback((id: string, delay = 100) => {
    setTimeout(() => {
      const n = getNode(id);
      if (n) setCenter(n.position.x + 100, n.position.y + 20, { duration: 500, zoom: getZoom() });
    }, delay);
  }, [getNode, setCenter, getZoom]);

  const handleSelectNode = useCallback((id: string) => {
    setSelectedNodeId(id);
    centerOn(id);
  }, [centerOn]);

  const { nodes, edges } = useMemo(
    () => buildTreeLayout(tree, expandedIds, selectedNodeId, handleToggleExpand, handleOpenMembers, handleSelectNode, isMobile, isDarkMode),
    [tree, expandedIds, selectedNodeId, handleToggleExpand, handleOpenMembers, handleSelectNode, isMobile, isDarkMode]
  );

  const hits = useMemo(() => searchOrg(tree, searchQuery), [tree, searchQuery]);

  /** Mở các nhánh cha rồi đưa nút (phòng ban hoặc nhân sự) vào giữa màn hình */
  const reveal = (id: string) => {
    const path = getAncestorsAndSelf(tree, id);
    if (!path) return;
    setExpandedIds(prev => {
      const next = new Set(prev);
      path.slice(0, -1).forEach(p => next.add(p));
      return next;
    });
    setSelectedNodeId(id);
    centerOn(id, 150);
  };

  // Nhân sự đang chọn → hiện thẻ thông tin
  const selectedEmployee = selectedNodeId ? findNode(tree, selectedNodeId) : null;
  const employeePath = selectedEmployee?.type === 'employee'
    ? (getAncestorsAndSelf(tree, selectedEmployee.id) || []).slice(0, -1).map(id => findNode(tree, id)).filter((n): n is RawNode => !!n && n.type === 'department').map(n => n.label || '')
    : [];

  const handleZoomIn = () => zoomIn({ duration: 300 });
  const handleZoomOut = () => zoomOut({ duration: 300 });
  const handleFitView = () => fitView({ padding: isMobile ? 0.15 : 0.2, duration: 500 });

  /** Xuất toàn bộ sơ đồ đang mở ra ảnh PNG */
  const exportPng = async () => {
    const viewport = wrapRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null;
    if (!viewport) return;
    setExporting(true);
    try {
      const bounds = getNodesBounds(getNodes());
      const pad = 40;
      const width = Math.min(6000, Math.ceil(bounds.width + 260 + pad * 2));
      const height = Math.min(6000, Math.ceil(bounds.height + 60 + pad * 2));
      const vp = getViewportForBounds(bounds, width, height, 0.2, 2, pad);
      const dataUrl = await toPng(viewport, {
        backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        width, height, pixelRatio: 2,
        style: { width: `${width}px`, height: `${height}px`, transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})` },
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `so-do-to-chuc-${viewType === 'position' ? 'theo-chuc-vu' : 'theo-phong-ban'}-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      message.success('Đã xuất ảnh sơ đồ');
    } catch {
      message.error('Không xuất được ảnh sơ đồ');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={wrapRef} className="w-full h-full relative bg-slate-50 dark:bg-slate-900 font-sans overflow-hidden transition-colors duration-200">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        minZoom={0.2}
        maxZoom={2}
        defaultViewport={{ x: 10, y: 10, zoom: isMobile ? 0.38 : 0.85 }}
        nodesDraggable={false}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        panOnScroll={false}
        preventScrolling={true}
        onPaneClick={() => {
          setShowSearchPopover(false);
          setShowViewDropdown(false);
        }}
        onMove={(_, viewport) => setZoomLevel(Math.round(viewport.zoom * 100))}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color={isDarkMode ? '#334155' : '#cbd5e1'} />
      </ReactFlow>

      {/* Chú thích chế độ đang xem */}
      {(viewType === 'position' || onlyLeader) && (
        <div className="absolute top-3 left-3 z-30 text-[11px] font-semibold bg-white/90 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1 text-slate-600 dark:text-slate-300">
          {viewType === 'position' ? 'Theo chức vụ' : 'Theo phòng ban'}{onlyLeader ? ' · chỉ hiện quản lý' : ''}
        </div>
      )}

      {selectedEmployee?.type === 'employee' && (
        <EmployeeCard
          emp={selectedEmployee}
          path={employeePath}
          onClose={() => setSelectedNodeId(null)}
          onShowDept={() => {
            const ids = getAncestorsAndSelf(tree, selectedEmployee.id) || [];
            const dept = findNode(tree, ids[ids.length - 2]);
            if (dept) setSelectedDeptForModal(dept);
          }}
        />
      )}

      <MembersModal
        isOpen={Boolean(selectedDeptForModal)}
        onClose={() => setSelectedDeptForModal(null)}
        department={selectedDeptForModal}
        onPickEmployee={id => { setSelectedDeptForModal(null); reveal(id); }}
      />

      {/* THANH CÔNG CỤ (nằm trong khung sơ đồ) */}
      <div className="absolute bottom-4 inset-x-0 z-40 flex justify-center px-2 pointer-events-none">
      <div className="pointer-events-auto flex items-center bg-white/95 dark:bg-slate-800/95 backdrop-blur border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg px-2 py-1.5 gap-0.5 sm:gap-1 text-xs text-slate-700 dark:text-slate-200 select-none max-w-full">
        {/* Chế độ xem */}
        <div ref={viewDropdownRef} className="relative shrink-0">
          <button
            onClick={() => { setShowViewDropdown(!showViewDropdown); setShowSearchPopover(false); }}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition font-medium cursor-pointer"
            title="Chế độ xem"
          >
            {viewType === 'department' ? <Building2 className="w-4 h-4 text-slate-500 shrink-0" /> : <UserCheck className="w-4 h-4 text-slate-500 shrink-0" />}
            <span className="hidden md:inline">{viewType === 'department' ? 'Theo phòng ban' : 'Theo chức vụ'}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          </button>
          {showViewDropdown && (
            <div className="absolute bottom-11 left-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-1 w-44 flex flex-col gap-0.5 z-50">
              {([['department', Building2, 'Theo phòng ban'], ['position', UserCheck, 'Theo chức vụ']] as const).map(([v, Icon, label]) => (
                <button key={v} onClick={() => changeView(v)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer ${viewType === v ? 'bg-slate-100 dark:bg-slate-700 font-semibold text-slate-800 dark:text-slate-100' : ''}`}>
                  <Icon className="w-4 h-4 text-slate-500" /> {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1 shrink-0" />

        {/* Chỉ hiện quản lý */}
        <label className="flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-1 cursor-pointer select-none shrink-0" title="Chỉ hiện trưởng / phó / quản lý">
          <span className="text-slate-600 dark:text-slate-300 font-medium flex items-center gap-1 text-[11px] sm:text-xs">
            <span className="border border-slate-300 dark:border-slate-600 rounded px-1 text-[9px] sm:text-[10px]">A|Z</span>
            <span className="hidden sm:inline">Leader</span>
          </span>
          <input type="checkbox" checked={onlyLeader} onChange={e => { setOnlyLeader(e.target.checked); setSelectedNodeId(null); setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 80); }} className="sr-only peer" />
          <div className="w-8 h-5 bg-slate-200 dark:bg-slate-700 rounded-full peer peer-checked:after:translate-x-[12px] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500 relative"></div>
        </label>
        <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1 shrink-0" />

        {/* Tìm phòng ban / nhân sự */}
        <div ref={searchRef} className="relative shrink-0">
          <button
            onClick={() => { setShowSearchPopover(!showSearchPopover); setShowViewDropdown(false); }}
            className={`p-1.5 sm:p-2 rounded-lg transition cursor-pointer ${showSearchPopover ? 'bg-slate-100 dark:bg-slate-700 text-orange-600 font-bold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
            title="Tìm phòng ban, nhân sự"
          >
            <Search className="w-4 h-4" />
          </button>
          {showSearchPopover && (
            <div className="absolute bottom-11 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-3 w-[min(320px,80vw)] flex flex-col gap-2 z-50">
              <div className="relative flex items-center">
                <input
                  type="text" placeholder="Tìm phòng ban hoặc nhân sự..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus
                  className="w-full pl-3 pr-8 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-orange-500"
                />
                <Search className="w-4 h-4 text-slate-400 absolute right-2.5 pointer-events-none" />
              </div>
              <div className="px-1 pt-1 font-semibold text-xs text-slate-700 dark:text-slate-300 uppercase tracking-wide">{COMPANY_NAME}</div>
              <div className="max-h-60 overflow-y-auto flex flex-col gap-0.5 pr-1">
                {hits.length > 0 ? hits.map(h => (
                  <button key={h.id} onClick={() => { reveal(h.id); setShowSearchPopover(false); }}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition flex items-center gap-1.5 cursor-pointer ${selectedNodeId === h.id ? 'bg-orange-50 dark:bg-orange-950/30 font-semibold text-orange-600' : 'hover:bg-slate-100 dark:hover:bg-slate-700/60 text-slate-700 dark:text-slate-200'}`}>
                    {!searchQuery && <span className="text-slate-400 font-normal select-none">{'—'.repeat(Math.min(h.depth, 3))}{h.depth ? ' ' : ''}</span>}
                    {h.kind === 'employee' ? <UserCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    <span className="min-w-0">
                      <span className="block truncate">{h.label}</span>
                      {searchQuery && <span className="block truncate text-[10px] text-slate-400 font-normal">{h.sub}</span>}
                    </span>
                  </button>
                )) : <div className="text-xs text-slate-400 text-center py-4">Không tìm thấy “{searchQuery}”</div>}
              </div>
            </div>
          )}
        </div>

        <button onClick={exportPng} disabled={exporting} className="hidden sm:flex p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0 disabled:opacity-50" title="Xuất ảnh PNG">
          <Download className="w-4 h-4" />
        </button>

        <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700 mx-0.5 sm:mx-1 shrink-0" />

        <button onClick={handleFitView} className="p-1.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0" title="Gom vừa màn hình"><Maximize2 className="w-4 h-4" /></button>
        <button onClick={handleZoomOut} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0" title="Thu nhỏ"><ZoomOut className="w-4 h-4" /></button>
        <span className="hidden sm:inline-block w-9 text-center font-semibold text-slate-700 dark:text-slate-200 shrink-0">{zoomLevel}%</span>
        <button onClick={handleZoomIn} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-300 cursor-pointer shrink-0" title="Phóng to"><ZoomIn className="w-4 h-4" /></button>
      </div>
      </div>
    </div>
  );
};

export default function OrganizationalChart() {
  return (
    <Layout>
      <Helmet><title>Sơ đồ tổ chức | Khothi</title></Helmet>
      <div className={USER_PAGE_ROW}>
        <UserSidebar />
        <div className="w-full min-w-0 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="w-full h-[calc(100vh-140px)] min-h-[520px] relative">
            <ReactFlowProvider>
              <OrgCanvas />
            </ReactFlowProvider>
          </div>
        </div>
      </div>
    </Layout>
  );
}
